import { useEffect, useState } from "react";

import { faUser } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styled from "styled-components";

import { theme } from "theme";

import { Spinner } from "../Spinner";

type AvatarColor = {
  text: string;
  background: string;
};

const AVATAR_COLORS: AvatarColor[] = [
  {
    text: theme.colors.redesign.b100,
    background: theme.colors.redesign.b10,
  },
  {
    text: theme.colors.redesign.g100,
    background: theme.colors.redesign.g10,
  },
  {
    text: theme.colors.redesign.o100,
    background: theme.colors.redesign.o10,
  },
  {
    text: theme.colors.redesign.p100,
    background: theme.colors.redesign.p10,
  },
  {
    text: theme.colors.redesign.r100,
    background: theme.colors.redesign.r10,
  },
  {
    text: theme.colors.redesign.y100,
    background: theme.colors.redesign.y10,
  },
  {
    text: theme.colors.redesign.t100,
    background: theme.colors.redesign.t10,
  },
  {
    text: theme.colors.redesign.db100,
    background: theme.colors.redesign.db10,
  },
];

const getAvatarColor = (id: number): AvatarColor => {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
};

const getInitials = (str: string): string[] | string => {
  if (typeof str !== "string") {
    return "";
  }
  const words = str.split(" ");
  return words.slice(0, 2).map(word => word.substr(0, 1).toUpperCase());
};

interface AvatarProps {
  id?: number;
  name?: string;
  src?: string;
  size?: number;
  color?: string;
  fontSize?: number;
  className?: string;
  loading?: boolean;
  onLoad?: () => void;
  backgroundColor?: string;
}

export const Avatar = ({
  src = "",
  name = "",
  size = 50,
  color = "",
  fontSize = 18,
  id = 1,
  className = "",
  loading = false,
  onLoad = () => undefined,
  backgroundColor,
}: AvatarProps): JSX.Element => {
  const [currentSrc, setSrc] = useState<string>();
  const [localLoading, setLocalLoading] = useState(false);
  const [loadingStyle, setLoadingStyle] = useState({});
  const avatarColor = getAvatarColor(id);

  useEffect(() => {
    if (!src) {
      setSrc(src);
      setLocalLoading(loading);
    } else if (src !== currentSrc) {
      setLocalLoading(true);
      setSrc(src);
    } else {
      setLocalLoading(localLoading || loading);
    }
  }, [src, loading, currentSrc, localLoading]);

  useEffect(() => {
    setLoadingStyle(localLoading ? { opacity: "50%" } : {});
  }, [localLoading]);

  const imageLoaded = (success = true) => {
    if (!success) {
      setSrc(undefined);
    }
    signalAsLoaded();
  };

  const signalAsLoaded = (externalSignal = true) => {
    if (localLoading === true) {
      setLocalLoading(false);
      if (externalSignal) {
        onLoad();
      }
    }
  };

  const getAvatar = (providedSrc?: string) => {
    if (providedSrc) {
      return (
        <StyledImg
          alt={name}
          src={providedSrc}
          $size={size}
          className={className}
          style={loadingStyle}
          onLoad={() => imageLoaded()}
          onError={() => imageLoaded(false)}
        />
      );
    }

    if (name?.trim()) {
      return (
        <StyledDiv
          $fontSize={fontSize}
          color={color || avatarColor.text}
          $size={size}
          $backgroundColor={backgroundColor || avatarColor.background}
          className={className}
          style={loadingStyle}
        >
          <span>{getInitials(name)}</span>
        </StyledDiv>
      );
    }

    return (
      <DefaultAvatar className={className} style={loadingStyle}>
        <FontAwesomeIcon icon={faUser} fontSize="32px" />
      </DefaultAvatar>
    );
  };

  return (
    <StyledDiv style={{ display: "flex", alignItems: "center", borderWidth: 0 }} $size={size} $fontSize={fontSize}>
      {getAvatar(currentSrc)}
      {localLoading ? (
        <>
          <StyledDiv
            style={{ position: "absolute", display: "flex", alignItems: "center", borderWidth: 0, margin: 0 }}
            $size={size}
            $fontSize={fontSize}
          >
            <Spinner small style={{ height: "50%", width: "50%" }} />
          </StyledDiv>
        </>
      ) : null}
    </StyledDiv>
  );
};

const StyledImg = styled.img<{ $size: number }>`
  display: inline-block;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  max-width: ${props => props.$size}px;
  max-height: ${props => props.$size}px;
  border-radius: ${props => props.theme.borderRadius.circular};
`;

const StyledDiv = styled.div<{
  $size: number;
  $fontSize: number;
  $color?: string;
  $backgroundColor?: string;
}>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  max-width: ${props => props.$size}px;
  max-height: ${props => props.$size}px;
  border-radius: ${props => props.theme.borderRadius.circular};
  background-color: ${props => props.$backgroundColor || props.theme.colors.white};
  text-align: center;
  overflow: hidden;
  user-select: none;
  & span {
    font-weight: 500;
    font-size: ${props => props.$fontSize}px;
    color: ${props => props.color || props.theme.colors.primary.base};
  }
`;

const DefaultAvatar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  width: 80px;
  border-radius: ${props => props.theme.borderRadius.circular};
  background-color: ${props => props.theme.colors.redesign.b10};

  svg {
    color: ${props => props.theme.colors.redesign.b100};
  }
`;
